import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, RotateCcw, X } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';

interface SlotErrorBannerProps {
  onRetry: () => void;
}

export const SlotErrorBanner: React.FC<SlotErrorBannerProps> = ({ onRetry }) => {
  const error = useSlotAssetGeneratorStore((s) => s.generation.error);
  const resetGeneration = useSlotAssetGeneratorStore((s) => s.resetGeneration);

  const [countdown, setCountdown] = useState<number | null>(null);
  const isRateLimit = error?.includes('429') || error?.toLowerCase().includes('rate limit');

  useEffect(() => {
    if (!isRateLimit) return;
    setCountdown(60);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isRateLimit]);

  const handleDismiss = useCallback(() => {
    resetGeneration();
  }, [resetGeneration]);

  if (!error) return null;

  return (
    <div className="mx-4 mt-4 rounded-lg border border-amber-600/30 bg-amber-950/30 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-amber-300">Generation Failed</div>
          <div className="text-xs text-amber-400/70 mt-1 break-words">{error}</div>
          {isRateLimit && countdown !== null && (
            <div className="text-xs text-amber-400 mt-1">
              Rate limited. Retry in {countdown}s...
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onRetry}
          disabled={isRateLimit && countdown !== null}
          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 rounded border border-amber-600/30 transition-colors disabled:opacity-50"
        >
          <RotateCcw size={12} />
          Retry
        </button>
        <button
          onClick={handleDismiss}
          className="flex items-center gap-1 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200 rounded hover:bg-zinc-800 transition-colors"
        >
          <X size={12} />
          Dismiss
        </button>
      </div>
    </div>
  );
};
